'use client';

import Image from 'next/image';
import { useEffect, useMemo, useRef, useState } from 'react';
import VideoLazyLoad from '@/app/components/shared/VideoLazyLoad';
import { Button } from '@/app/components/ui/button';
import { cn } from '@/app/utils';

export interface FeatureCarouselItem {
  id: string;
  title: string;
  description: string;
  image?: string;
  videoSrc?: string;
  videoPoster?: string;
  mobilePoster?: string;
  mobileVideoSrc?: string;
}

export interface FeatureCarouselProps {
  title: string;
  /** 标题上方的小标签（如“参考生视频”），可选 */
  eyebrow?: string;
  description?: string;
  /** 描述文案的额外样式（如透明度），可选 */
  descriptionClassName?: string;
  /** 特性列表 */
  features: FeatureCarouselItem[];
  buttonText: string;
  onButtonClick: () => void;
  className?: string;
  titleClassName?: string;
  contentClassName?: string;
  /** 是否反向布局（指示器在右，图视频在左） */
  reverseLayout?: boolean;
}

export function FeatureCarousel({
  title,
  eyebrow,
  description,
  descriptionClassName,
  features,
  buttonText,
  onButtonClick,
  className,
  titleClassName,
  contentClassName,
  reverseLayout = false,
}: FeatureCarouselProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);

  // 处理滚动检测，更新活跃的优势项
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;

      // 获取section的位置
      const sectionRect = sectionRef.current.getBoundingClientRect();
      const sectionTop = sectionRect.top;
      const sectionHeight = sectionRect.height;

      // 如果section不在视口内，不做处理
      if (sectionTop > window.innerHeight || sectionTop + sectionHeight < 0) return;

      // 计算section在视口中的位置比例
      const viewportRatio = Math.max(
        0,
        Math.min(1, (window.innerHeight - sectionTop) / (window.innerHeight + sectionHeight)),
      );

      // 根据滚动比例确定当前活跃的优势项
      const newActiveIndex = Math.min(features.length - 1, Math.floor(viewportRatio * features.length));

      setActiveIndex(newActiveIndex);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [features.length]);

  // 将 features 按媒体源分组，避免重复渲染相同的视频/图片
  const uniqueMedia = useMemo(() => {
    const mediaMap = new Map<string, number[]>();
    features.forEach((feature, index) => {
      const mediaKey = feature.videoSrc || feature.image || '';
      if (mediaKey) {
        const indices = mediaMap.get(mediaKey);
        if (indices) {
          indices.push(index);
        } else {
          mediaMap.set(mediaKey, [index]);
        }
      }
    });

    // 获取唯一的媒体源列表
    return Array.from(mediaMap.entries()).map(([mediaKey, indices]) => {
      const firstIndex = indices[0];
      const feature = features[firstIndex];
      return {
        mediaKey,
        indices,
        feature,
      };
    });
  }, [features]);

  // 判断某个媒体源是否应该显示（当前 activeIndex 是否在使用该媒体源的 indices 中）
  const isMediaActive = (indices: number[]) => {
    return indices.includes(activeIndex);
  };

  return (
    <section ref={sectionRef} className={cn('pt-[120px]', className)}>
      <div className="mx-auto flex flex-col items-center px-4">
        <div className={cn('text-center', titleClassName ?? 'w-[58vw]')}>
          {eyebrow && <p className="mb-3 font-medium text-[#0086FF] text-base sm:text-sm">{eyebrow}</p>}
          <h2 className="mb-4 font-semibold text-[40px] leading-tight sm:text-[36px]">{title}</h2>
          {description && (
            <p className={cn('mb-16 font-normal text-xl sm:text-base', descriptionClassName)}>{description}</p>
          )}
        </div>

        <div className="hidden w-full flex-col sm:block">
          {features.map((feature) => (
            <div key={feature.id} className="mb-16 text-center">
              <h3 className="mb-4 font-semibold text-2xl">{feature.title}</h3>
              <p className="mb-4 font-normal text-sm">{feature.description}</p>
              <Button
                className="mb-8 w-fit rounded-[100px] bg-[#0086FF] px-6 py-4 font-medium text-base text-white shadow-lg transition-all hover:shadow-xl"
                onClick={onButtonClick}
              >
                {buttonText}
              </Button>
              <div className="mb-10 overflow-hidden rounded-xl">
                {feature.videoSrc && feature.videoPoster ? (
                  <VideoLazyLoad
                    videoSrc={feature.videoSrc}
                    poster={feature.videoPoster}
                    mobilePoster={feature.mobilePoster}
                  />
                ) : feature.image ? (
                  <div className="relative aspect-video w-full">
                    <Image
                      src={feature.image}
                      alt={feature.title}
                      fill
                      className="object-cover"
                      sizes="(max-width: 768px) 100vw, 74vw"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                ) : null}
              </div>
            </div>
          ))}
        </div>

        <div
          className={cn('relative flex sm:hidden', contentClassName ?? 'w-[84vw]', reverseLayout && 'flex-row-reverse')}
        >
          {/* 指示器 */}
          <div className={cn('absolute flex h-full items-center', reverseLayout ? 'right-0' : 'left-0')}>
            <div className="relative h-[90%] w-1 bg-gray-700">
              <div
                className="absolute left-0 w-full bg-white transition-all duration-500"
                style={{
                  top: `${(activeIndex * 100) / features.length}%`,
                  height: `${100 / features.length}%`,
                }}
              />
            </div>
          </div>

          {/* 文本内容 */}
          <div className={cn('flex w-[30vw] flex-col justify-between py-8', reverseLayout ? 'ml-6 pr-6' : 'mr-6 pl-6')}>
            {features.map((feature, index) => (
              <div
                key={feature.id}
                className={cn(
                  'transform transition-all duration-500',
                  index === activeIndex ? 'opacity-100' : 'opacity-40',
                )}
                onMouseEnter={() => setActiveIndex(index)}
              >
                <h3 className="mb-6 font-semibold text-[32px] sm:mb-4 md:mb-4 md:text-2xl">{feature.title}</h3>
                <p
                  className={cn(
                    'font-normal text-base text-system-white64 transition-all duration-500 sm:text-sm md:mb-4 md:text-sm',
                    index === activeIndex ? 'mb-6 max-h-40 opacity-100' : 'max-h-0 overflow-hidden opacity-0',
                  )}
                >
                  {feature.description}
                </p>
                {index === activeIndex && (
                  <Button
                    className="mb-2 h-[46px] rounded-[100px] border-2 border-[#0086FF] bg-transparent px-8 py-4 font-semibold text-[#0086FF] text-sm shadow-lg transition-all hover:shadow-xl sm:mb-8"
                    onClick={onButtonClick}
                  >
                    {buttonText}
                  </Button>
                )}
              </div>
            ))}
          </div>

          {/* 图视频 */}
          <div className="flex flex-1 items-center">
            <div className="relative aspect-video w-full overflow-hidden rounded-xl">
              {uniqueMedia.map(({ mediaKey, indices, feature }) => {
                const isActive = isMediaActive(indices);
                return (
                  <div
                    key={mediaKey}
                    className={cn(
                      'absolute inset-0 transition-all duration-1000',
                      isActive ? 'z-10 opacity-100' : 'z-0 opacity-0',
                    )}
                  >
                    {feature.videoSrc && feature.videoPoster ? (
                      <VideoLazyLoad
                        videoSrc={feature.videoSrc}
                        poster={feature.videoPoster}
                        mobilePoster={feature.mobilePoster}
                        className="h-full w-full object-cover"
                      />
                    ) : feature.image ? (
                      <Image
                        src={feature.image}
                        alt={feature.title}
                        fill
                        className="object-cover"
                        sizes="(max-width: 768px) 100vw, 84vw"
                      />
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
